import React, { useEffect, useState, useCallback } from "react";
import { Link, useParams, useSearchParams, useLocation } from "react-router-dom";
import { Home, Bed, Bath, MapPin, Search, Sparkles, ArrowRight } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import SeoHead from "@/components/SeoHead";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useBrand, useMarket, useTenantPath, useTenantSlug } from "@/lib/brand";
import { t } from "@/lib/terms";
import { api, mediaUrl } from "@/lib/api";
import { statusBadge, fmtListingPrice } from "@/lib/propertyStatus";

/**
 * Public property listings marketplace for a tenant.
 * Renders at /t/:slug/properties, legacy /properties, and — in a
 * chrome-less "embedded" mode — /embed/listings?tenant=:slug, which
 * agents can drop into an <iframe> on their OWN website (Foundation
 * Connect's "plugin" for agents who don't want a full Foundation Sites
 * build). Card links break out of the iframe (target="_blank") rather
 * than navigating inside it.
 */
export default function PublicListings() {
  const brand = useBrand();
  const market = useMarket();
  const isUs = market === "us";
  const tenantPath = useTenantPath();
  const { slug: urlSlug } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const embedded = searchParams.get("embed") === "1" || location.pathname.includes("/embed");
  const rootRef = React.useRef(null);
  const biz = brand.business || {};
  const { slug: tenantSlug, known: tenantKnown } = useTenantSlug(urlSlug);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ listing_type: "sale", min_bedrooms: "", max_price: "", postcode: "" });

  const load = useCallback(async () => {
    // Nothing until we know whose properties to ask for: on a custom domain
    // the slug arrives with the brand, and fetching before it does renders
    // another agency's listings for a moment. See useTenantSlug.
    if (!tenantKnown) return;
    setLoading(true);
    try {
      const params = { tenant: tenantSlug, listing_type: filters.listing_type, limit: 60 };
      if (filters.min_bedrooms) params.min_bedrooms = Number(filters.min_bedrooms);
      if (filters.max_price) params.max_price = Number(filters.max_price);
      if (filters.postcode) params.postcode = filters.postcode;
      const { data } = await api.get("/crm/public/properties", { params });
      setItems(data.items || []);
    } catch (e) { setItems([]); }
    finally { setLoading(false); }
  }, [tenantSlug, tenantKnown, filters]);

  useEffect(() => { load(); }, [load]);

  // Iframe auto-resize: emit height to parent on any change (same
  // contract as Valuation.jsx's embed — see the matching listener in
  // CrmOverview.jsx's embedSnippet()).
  useEffect(() => {
    if (!embedded) return;
    const send = () => {
      const h = rootRef.current?.offsetHeight || document.body.scrollHeight;
      try {
        // Both names, deliberately. "wb-" is the first customer,
        // and is the name every snippet already pasted on a live agency site
        // is listening for; dropping it would stop those embeds resizing on
        // sites we cannot reach. "embed-" is what new snippets listen for.
        // Two postMessages to the parent cost nothing; a silently broken
        // embed on a customer's homepage costs a support ticket at best.
        window.parent?.postMessage({ type: "wb-listings:height", height: h }, "*");
        window.parent?.postMessage({ type: "embed-listings:height", height: h }, "*");
      } catch (e) { /* ignore */ }
    };
    send();
    const t = setInterval(send, 400);
    const ro = new ResizeObserver(send);
    if (rootRef.current) ro.observe(rootRef.current);
    return () => { clearInterval(t); ro.disconnect(); };
  }, [embedded, loading, items]);

  const registerHref = tenantPath("register");
  const originForLinks = embedded && typeof window !== "undefined" ? window.location.origin : "";

  return (
    <div ref={rootRef} className={embedded ? "bg-background" : "min-h-screen bg-background flex flex-col"}>
      {!embedded && (
        <SeoHead
          title={biz.name
            ? (isUs
              ? `Homes For Sale & For Rent, ${biz.name} | ${brand.regions_covered_prose || "Local Listings"}`
              : `Properties For Sale & To Let, ${biz.name} | ${brand.regions_covered_prose || "Local Listings"}`)
            : undefined}
          description={`Browse every ${isUs ? "home currently listed" : "property currently instructed"} with ${biz.name || "us"} across ${brand.regions_covered_prose || "your area"}, updated live.`}
          image={brand.assets?.hero_image_url ? mediaUrl(brand.assets.hero_image_url) : undefined}
        />
      )}
      {!embedded && <SiteHeader />}
      <main className="flex-1">
        <div className={embedded ? "mx-auto px-4 py-6" : "mx-auto max-w-7xl px-6 lg:px-10 py-14"}>
          {!embedded && (
            <div>
              <div className="text-xs uppercase tracking-[0.28em] text-muted-foreground">Properties</div>
              <h1 className="font-serif-display text-4xl sm:text-5xl text-primary mt-3">
                What's on the market at {biz.name || "our agency"}
              </h1>
              <p className="text-muted-foreground mt-4 max-w-2xl leading-relaxed">
                Every home currently {isUs ? "listed" : "instructed"} with us. Register your search and we'll email you the moment a match comes on.
              </p>
            </div>
          )}

          {/* Filters */}
          <div className={embedded ? "mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" : "mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3"}>
            <Select value={filters.listing_type} onValueChange={(v) => setFilters((f) => ({ ...f, listing_type: v }))}>
              <SelectTrigger data-testid="public-listing-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sale">For sale</SelectItem>
                <SelectItem value="letting">{t("to_let", market)}</SelectItem>
              </SelectContent>
            </Select>
            <Input type="number" placeholder="Min bedrooms" value={filters.min_bedrooms}
                   onChange={(e) => setFilters((f) => ({ ...f, min_bedrooms: e.target.value }))}
                   data-testid="public-min-beds" />
            <Input type="number"
                   placeholder={filters.listing_type === "letting" ? (isUs ? "Max $/mo" : "Max £ pcm") : "Max price"}
                   value={filters.max_price}
                   onChange={(e) => setFilters((f) => ({ ...f, max_price: e.target.value }))}
                   data-testid="public-max-price" />
            <Input placeholder={isUs ? "ZIP code (e.g. 78704)" : "Postcode area (e.g. BN1)"} value={filters.postcode}
                   onChange={(e) => setFilters((f) => ({ ...f, postcode: e.target.value }))}
                   data-testid="public-postcode" />
            {embedded ? (
              <a href={originForLinks + registerHref} target="_blank" rel="noreferrer">
                <Button variant="outline" className="w-full h-full rounded-sm border-accent/40 text-accent hover:bg-accent/10">
                  <Sparkles className="w-4 h-4 mr-2" />Register for alerts
                </Button>
              </a>
            ) : (
              <Link to={registerHref}>
                <Button variant="outline" className="w-full h-full rounded-sm border-accent/40 text-accent hover:bg-accent/10">
                  <Sparkles className="w-4 h-4 mr-2" />Register for alerts
                </Button>
              </Link>
            )}
          </div>

          {/* Grid */}
          <div className={embedded ? "mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" : "mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"}>
            {loading ? (
              <div className="col-span-full py-16 text-center text-muted-foreground">Loading listings…</div>
            ) : items.length === 0 ? (
              <div className="col-span-full py-16 text-center text-muted-foreground" data-testid="public-empty">
                No matching properties right now.{" "}
                {embedded ? (
                  <a href={originForLinks + registerHref} target="_blank" rel="noreferrer" className="text-primary underline">Register for alerts</a>
                ) : (
                  <Link to={registerHref} className="text-primary underline">Register for alerts</Link>
                )}{" "}
                and we'll email you the moment something comes on.
              </div>
            ) : items.map((p) => {
              const price = fmtListingPrice(p, market);
              const cover = (p.images || []).find((i) => i.id === p.primary_image_id) || (p.images || [])[0];
              const badge = statusBadge(p.status, p.listing_type, market);
              const CardTag = embedded ? "a" : Link;
              const cardProps = embedded
                ? { href: originForLinks + tenantPath(`properties/${p.id}`), target: "_blank", rel: "noreferrer" }
                : { to: tenantPath(`properties/${p.id}`) };
              return (
                <CardTag
                  key={p.id}
                  {...cardProps}
                  data-testid={`public-property-${p.id}`}
                  className="group border border-border bg-card rounded-sm overflow-hidden hover:border-primary transition-colors"
                >
                  {cover ? (
                    <div className="aspect-video overflow-hidden border-b border-border relative">
                      {badge && (
                        <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[11px] uppercase tracking-widest">
                          {badge.label}
                        </span>
                      )}
                      <img
                        src={mediaUrl(cover.thumb_url || cover.url)}
                        alt={p.address_line1}
                        className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="aspect-video bg-secondary/40 flex items-center justify-center border-b border-border relative">
                      {badge && (
                        <span className="absolute top-3 left-3 z-10 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-[11px] uppercase tracking-widest">
                          {badge.label}
                        </span>
                      )}
                      <Home className="w-8 h-8 text-muted-foreground/40" />
                    </div>
                  )}
                  <div className="p-5">
                    <div className="font-serif-display text-xl text-primary group-hover:text-primary/80 transition-colors">
                      {p.address_line1}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                      <MapPin className="w-3 h-3" /> {p.town || ""} · {p.postcode}
                    </div>
                    <div className="font-serif-display text-2xl text-primary mt-4">{price}</div>
                    <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {p.bedrooms}</span>
                      <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {p.bathrooms}</span>
                      {p.size_sqft > 0 && <span>{p.size_sqft.toLocaleString()} sqft</span>}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-4 flex items-center gap-1">
                      {p.property_type} <ArrowRight className="w-3 h-3 ml-auto text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </CardTag>
              );
            })}
          </div>
        </div>
      </main>
      {!embedded && <SiteFooter />}
    </div>
  );
}
